import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, FileSpreadsheet, Link2, Loader, RefreshCw, Sheet } from 'lucide-react';
import { GlassButton } from '../ui';
import { gsheetImportApi, GsheetImportJob, GsheetStatus, GsheetTab } from '../../services/gsheetImportApi';
import { openSpreadsheetPicker } from '../../utils/googlePicker';

// google-sheets-lead-import Phase 1 — the "Google Sheet" source of the CRM
// import panel. States: not connected → Connect; needs_reauth → Reconnect;
// connected → pick a spreadsheet (Google Picker, drive.file) → pick a tab →
// "Import this tab", which returns an ordinary CrmImportJob for the panel.

interface Props {
  onJob: (job: GsheetImportJob) => void;
  onToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const apiError = (e: any, fallback: string): string => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (typeof d === 'object' && typeof d.detail === 'string') return d.detail;
  return fallback;
};

const apiErrorCode = (e: any): string | null => {
  const d = e?.response?.data?.detail;
  return d && typeof d === 'object' && typeof d.error === 'string' ? d.error : null;
};

const GoogleSheetsSourcePicker: React.FC<Props> = ({ onJob, onToast }) => {
  const [status, setStatus] = useState<GsheetStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'connect' | 'pick' | 'tabs' | 'import' | 'disconnect' | null>(null);
  const [spreadsheet, setSpreadsheet] = useState<{ id: string; title: string } | null>(null);
  const [tabs, setTabs] = useState<GsheetTab[]>([]);
  const [gid, setGid] = useState<number | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await gsheetImportApi.status();
      setStatus(data);
    } catch (e: any) {
      onToast(apiError(e, "Couldn't check the Google Sheets connection"), 'error');
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const markNeedsReauth = () => {
    setStatus((s) => (s ? { ...s, connected: false, status: 'needs_reauth' } : s));
    setSpreadsheet(null);
    setTabs([]);
    setGid(null);
  };

  const connect = async () => {
    setBusy('connect');
    try {
      const { data } = await gsheetImportApi.authStart();
      window.location.href = data.auth_url;
    } catch (e: any) {
      const code = apiErrorCode(e);
      onToast(
        code === 'encryption_not_configured'
          ? 'Google Sheets import is not available yet — please contact support.'
          : apiError(e, "Couldn't start connecting Google Sheets"),
        'error',
      );
      setBusy(null);
    }
  };

  const loadTabs = async (id: string, fallbackTitle: string) => {
    setBusy('tabs');
    try {
      const { data } = await gsheetImportApi.listTabs(id);
      setSpreadsheet({ id, title: data.title || fallbackTitle });
      setTabs(data.tabs);
      setGid(data.tabs.length ? data.tabs[0].gid : null);
    } catch (e: any) {
      if (apiErrorCode(e) === 'needs_reauth') markNeedsReauth();
      onToast(apiError(e, "Couldn't read that spreadsheet's tabs"), 'error');
      setSpreadsheet(null);
      setTabs([]);
    } finally {
      setBusy(null);
    }
  };

  const choose = async () => {
    setBusy('pick');
    try {
      const { data: cfg } = await gsheetImportApi.pickerConfig();
      const picked = await openSpreadsheetPicker({ apiKey: cfg.api_key, appId: cfg.app_id, accessToken: cfg.access_token });
      if (!picked) { setBusy(null); return; }
      await loadTabs(picked.id, picked.name);
    } catch (e: any) {
      if (apiErrorCode(e) === 'needs_reauth') markNeedsReauth();
      onToast(apiError(e, e instanceof Error ? e.message : "Couldn't open the Google picker"), 'error');
      setBusy(null);
    }
  };

  const importTab = async () => {
    if (!spreadsheet || gid === null) return;
    setBusy('import');
    try {
      const { data } = await gsheetImportApi.importTab(spreadsheet.id, gid);
      onJob(data);
    } catch (e: any) {
      if (apiErrorCode(e) === 'needs_reauth') markNeedsReauth();
      onToast(apiError(e, "Couldn't import that tab"), 'error');
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy('disconnect');
    try {
      await gsheetImportApi.disconnect();
      setSpreadsheet(null);
      setTabs([]);
      setGid(null);
      onToast('Google Sheets disconnected. You can also remove AiMe in your Google Account permissions.', 'info');
      await loadStatus();
    } catch (e: any) {
      onToast(apiError(e, "Couldn't disconnect Google Sheets"), 'error');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="gsheet-picker">
        <div className="gsheet-picker__status"><Loader size={16} className="crm-spin" /> Checking Google Sheets connection…</div>
      </div>
    );
  }

  if (!status || status.status === 'disconnected') {
    return (
      <div className="gsheet-picker">
        <div className="gsheet-picker__status">
          <Sheet size={18} />
          <span>Connect Google Sheets to import a tab of leads (for example, Meta lead-ad leads). AiMe can only read the spreadsheets you pick.</span>
        </div>
        <div className="gsheet-picker__row">
          <GlassButton variant="primary" onClick={connect} disabled={busy !== null}>
            {busy === 'connect' ? <Loader size={15} className="crm-spin" /> : <Link2 size={15} />} Connect Google Sheets
          </GlassButton>
        </div>
      </div>
    );
  }

  if (status.status === 'needs_reauth') {
    return (
      <div className="gsheet-picker">
        <div className="gsheet-picker__status gsheet-picker__status--warn">
          <AlertTriangle size={18} />
          <span>Google Sheets access has expired or was removed. Reconnect to continue.</span>
        </div>
        <div className="gsheet-picker__row">
          <GlassButton variant="primary" onClick={connect} disabled={busy !== null}>
            {busy === 'connect' ? <Loader size={15} className="crm-spin" /> : <RefreshCw size={15} />} Reconnect
          </GlassButton>
          <GlassButton variant="secondary" size="sm" onClick={disconnect} disabled={busy !== null}>Disconnect</GlassButton>
        </div>
      </div>
    );
  }

  return (
    <div className="gsheet-picker">
      <div className="gsheet-picker__status">
        <Sheet size={18} />
        <span>
          Connected as <span className="gsheet-picker__email">{status.google_email || 'your Google account'}</span>
        </span>
      </div>

      {!status.picker_available && (
        <div className="gsheet-picker__status gsheet-picker__status--warn">
          <AlertTriangle size={16} />
          <span>The Google Sheets picker isn't configured on this server yet.</span>
        </div>
      )}

      <div className="gsheet-picker__row">
        <GlassButton variant="primary" onClick={choose} disabled={busy !== null || !status.picker_available}>
          {busy === 'pick' || busy === 'tabs' ? <Loader size={15} className="crm-spin" /> : <FileSpreadsheet size={15} />}
          {spreadsheet ? ' Choose a different sheet' : ' Choose a Google Sheet'}
        </GlassButton>
        <GlassButton variant="secondary" size="sm" onClick={disconnect} disabled={busy !== null}>Disconnect</GlassButton>
      </div>

      {spreadsheet && (
        <div className="gsheet-picker__tab">
          <div className="gsheet-picker__sheet-name">
            <FileSpreadsheet size={16} /> <span>{spreadsheet.title || 'Untitled spreadsheet'}</span>
          </div>
          {tabs.length === 0 ? (
            <p className="crm-import-hint">This spreadsheet has no tabs to import.</p>
          ) : (
            <div className="gsheet-picker__row">
              <select
                className="crm-import-select"
                aria-label="Tab to import"
                value={gid ?? ''}
                onChange={(e) => setGid(Number(e.target.value))}
              >
                {tabs.map((t) => (
                  <option key={t.gid} value={t.gid}>{t.title}</option>
                ))}
              </select>
              <GlassButton variant="primary" onClick={importTab} disabled={busy !== null || gid === null}>
                {busy === 'import' ? <Loader size={15} className="crm-spin" /> : <ArrowRight size={15} />} Import this tab
              </GlassButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleSheetsSourcePicker;
